import useIsGroupAdmin from '@hooks/useIsGroupAdmin';
import { useInterviewOptions } from '@providers/InterviewsProvider';
import groupBy from 'lodash/groupBy';
import { useMemo, useState } from 'react';
import GroupSubheader from './GroupSubheader';
import InterviewMenuCopyButton from './InterviewMenuCopyButton';
import { MenuItem } from '@mui/material';
import { InterviewCreationFormValues, concatenate } from './types';
import { CreateGroupOrInterviewModal } from './CreateGroupOrInterviewModal';
import { useNavigate } from 'react-router-dom';

/**
 * Hook that returns interview options for use in MUI Select.
 *
 * MUI Select requires MenuItems to be direct descendants - wrapping them in a
 * component breaks Select's ability to find and match values.
 * See: https://github.com/mui/material-ui/issues/31006
 *
 * @returns options - Array of MenuItem/ListSubheader elements to spread directly in Select
 * @returns CreateInterviewModal - Modal component to render outside the Select
 */
export function useInterviewListOptions() {
    const isGroupAdmin = useIsGroupAdmin();
    const [interviewCreationFormValues, setInterviewCreationFormValues] = useState<
        InterviewCreationFormValues | false
    >(false);
    const navigate = useNavigate();

    const { availableInterviews, allGroups, selectInterview } = useInterviewOptions();

    const groupedInterviews = useMemo(
        () => groupBy(availableInterviews, ({ groupId }) => groupId),
        [availableInterviews],
    );

    const options = useMemo(() => {
        if (!allGroups?.length) {
            return [];
        }

        const result: React.ReactElement[] = [];

        allGroups.forEach((groupId) => {
            result.push(
                <GroupSubheader
                    key={groupId + 'header'}
                    id={groupId}
                    setInterviewCreationForm={setInterviewCreationFormValues}
                />,
            );
            const haveAvailableInterviews = groupedInterviews[groupId];

            if (haveAvailableInterviews) {
                groupedInterviews[groupId].forEach(({ id: interviewId, groupId, name }) => {
                    result.push(
                        <MenuItem
                            onClick={() => {
                                selectInterview(interviewId, groupId);
                                navigate('/app/survey');
                            }}
                            key={interviewId + groupId}
                            sx={{
                                paddingLeft: 3,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                '& .appear-item': {
                                    opacity: 0,
                                    marginLeft: '1rem',
                                },
                                '&:hover .appear-item': {
                                    opacity: 1,
                                    display: 'block',
                                    marginLeft: '1rem',
                                },
                            }}
                            value={concatenate(groupId, interviewId)}
                        >
                            {name}
                            <InterviewMenuCopyButton
                                groupId={groupId}
                                interviewId={interviewId}
                                setInterviewCreationForm={setInterviewCreationFormValues}
                            />
                        </MenuItem>,
                    );
                });
            } else {
                // show placeholders for groups with no interviews
                result.push(
                    <MenuItem key={groupId + 'no-interviews'} value={groupId} disabled>
                        Create a new survey to get started
                    </MenuItem>,
                );
            }
        });

        if (isGroupAdmin) {
            result.push(
                <GroupSubheader
                    key={'new group'}
                    id={''}
                    setInterviewCreationForm={setInterviewCreationFormValues}
                />,
            );
        }

        return result;
    }, [allGroups, isGroupAdmin, groupedInterviews, selectInterview, navigate]);

    const CreateInterviewModal = useMemo(
        () => (
            <CreateGroupOrInterviewModal
                onClose={() => setInterviewCreationFormValues(false)}
                interviewCreationFormValues={interviewCreationFormValues}
            />
        ),
        [interviewCreationFormValues],
    );

    return { options, CreateInterviewModal };
}
